import { Button, Stack, Typography } from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import { ProgressBar } from '~/@ui/progress-bar/ProgressBar'
import { SimplePageWrapper } from '~/@ui/simple-page-wrapper/SimplePageWrapper'
import { useAlert } from '~/@ui/alert/useAlert'
import { StyledSelect } from '~/@ui/form/StyledSelect'
import { Controller, Form, useForm } from 'react-hook-form'
import { StyledMenuItem } from '~/@ui/form/StyledMenuItem'
import { FormStyledTextField } from '~/@ui/form/FormStyledTextField'
import { useEffect, useState } from 'react'
import { getApi } from '~/@utils/api/getApi'
import { getErrorMessage } from '~/@utils/api/getErrorMessage'
import {
  I_OWNER_ESTATE_STATUS,
  IUpdateOwnerEstateDto,
} from '~/@api/generated-api-types/global.types'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  EstateFormScheme,
  getEstateSchema,
  getEstateDefaultValues,
} from './_scheme/estate.scheme'
import { getFormErrorMessage } from '~/@utils/form/getFormErrorMessage'
import { Header } from '~/@ui/header/Header'

export const PropertyInformationsPage = () => {
  const navigate = useNavigate()
  const { showAlert } = useAlert()

  const { estateId = '' } = useParams()

  const [propertyType, setPropertyType] = useState<'house' | 'flat'>()

  const isApartment = propertyType === 'flat'
  const isHouse = propertyType === 'house'

  const { control, trigger, getValues, getFieldState, reset } =
    useForm<EstateFormScheme>({
      resolver: zodResolver(getEstateSchema(propertyType)),
      mode: 'onBlur',
      defaultValues: getEstateDefaultValues(propertyType),
    })

  const initializeEstate = async () => {
    try {
      const { data: response } =
        await getApi().ownerEstateAppApiControllerGetOwnerEstate(estateId)

      if ('success' in response) {
        const newPropertyType = response.ownerEstate.type as 'house' | 'flat'
        setPropertyType(newPropertyType)

        // Resetovanie formu s novými default values a schémou
        reset(getEstateDefaultValues(newPropertyType))

        return
      }

      throw new Error(
        'Nasatala neznáma chyba pri odosielaní kontaktného formulára.'
      )
    } catch (err) {
      console.error(err)
      showAlert('error', getErrorMessage(err))
    }
  }

  const updateEstate = async () => {
    try {
      const { data: response } =
        await getApi().ownerEstateAppApiControllerUpdateOwnerEstate(
          estateId,
          getValues() as IUpdateOwnerEstateDto
        )

      if ('success' in response) {
        navigate(`/vlastnik/dotaznik/detaily-o-nehnutelnosti/${estateId}`)
        return
      }

      throw new Error(
        'Nasatala neznáma chyba pri aktualizovaní dát o nehnuteľnosti.'
      )
    } catch (err) {
      console.error(err)
      showAlert('error', getErrorMessage(err))
    }
  }

  const handleSubmit = async () => {
    const isValid = await trigger()
    if (!isValid) {
      const errorMessage = getFormErrorMessage(
        getEstateDefaultValues(propertyType),
        getFieldState
      )
      showAlert('error', errorMessage)
      return
    }
    updateEstate()
  }

  useEffect(() => {
    if (!estateId) {
      navigate('/vlastnik/dotaznik/adresa-nehnutelnosti')
      return
    }

    initializeEstate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <SimplePageWrapper
      onClickBack={() => navigate('/vlastnik/dotaznik/adresa-nehnutelnosti')}>
      <Stack className="px-5 pb-28">
        <ProgressBar
          className="mt-[26px]"
          manualPercentage={[100, 100, 100, 0]}
        />
        <Header
          title="Údaje o nehnuteľnsti"
          text={`Prosíme, konkretizujte detaily vášho ${isApartment ? 'bytu' : 'domu'}.`}
          className="mt-3"
        />

        <Form control={control} className="mt-8">
          <Stack className="gap-3">
            {isApartment && (
              <>
                <FormStyledTextField
                  control={control}
                  name="level"
                  placeholder="Poschodie"
                  inputMode="numeric"
                  type="text"
                  returnNumber
                  slotProps={{
                    htmlInput: {
                      pattern: '[0-9]*',
                      maxLength: 2,
                    },
                  }}
                />
                <FormStyledTextField
                  control={control}
                  name="flatNumber"
                  placeholder="Číslo bytu"
                />
                <FormStyledTextField
                  control={control}
                  name="flatArea"
                  placeholder="Rozloha nehnuteľnosti v m²"
                  inputMode="numeric"
                  type="text"
                  returnNumber
                  slotProps={{
                    htmlInput: {
                      pattern: '[0-9]*',
                      maxLength: 6,
                    },
                  }}
                />
              </>
            )}
            {isHouse && (
              <>
                <FormStyledTextField
                  control={control}
                  name="numberOfFloors"
                  placeholder="Počet podlaží"
                  inputMode="numeric"
                  type="text"
                  returnNumber
                  slotProps={{
                    htmlInput: {
                      pattern: '[0-9]*',
                      maxLength: 2,
                    },
                  }}
                />
                <FormStyledTextField
                  control={control}
                  name="houseUsableArea"
                  placeholder="Úžitková plocha v m²"
                  inputMode="numeric"
                  type="text"
                  returnNumber
                  slotProps={{
                    htmlInput: {
                      pattern: '[0-9]*',
                      maxLength: 6,
                    },
                  }}
                />
                <FormStyledTextField
                  control={control}
                  name="landArea"
                  placeholder="Plocha pozemku v m²"
                  inputMode="numeric"
                  type="text"
                  returnNumber
                  slotProps={{
                    htmlInput: {
                      pattern: '[0-9]*',
                      maxLength: 6,
                    },
                  }}
                />
              </>
            )}

            {propertyType && (
              <Controller
                name="status"
                control={control}
                render={({ field, fieldState }) => (
                  <StyledSelect
                    {...field}
                    className="w-full"
                    showArrow
                    error={!!fieldState.error && !field.value}>
                    <StyledMenuItem className="hidden">
                      <Typography className="text-3d font-medium text-zGrey400 tabular-nums">
                        Stav nehnutelnosti
                      </Typography>
                    </StyledMenuItem>
                    {Object.entries(I_OWNER_ESTATE_STATUS).map(
                      ([key, value]) => (
                        <StyledMenuItem key={key} value={value}>
                          <Typography className="text-3d font-semibold text-zBlack200 tabular-nums">
                            {value}
                          </Typography>
                        </StyledMenuItem>
                      )
                    )}
                  </StyledSelect>
                )}
              />
            )}
          </Stack>
        </Form>

        <Button
          variant="contained"
          className="w-fit h-14 self-center px-10 mt-[72px]"
          onClick={handleSubmit}
          disabled={!propertyType}>
          Pokračovať
        </Button>
      </Stack>
    </SimplePageWrapper>
  )
}
